import { Component, ChangeDetectionStrategy, ViewEncapsulation, ViewChild } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ChatbotComponent } from "./s25.chat.bot.component";
import { AiPricingFormulaResponse } from "./s25.chatbot.service";
import { AiTelemetry } from "../../services/ai.telemetry";
import type { TelemetrySession } from "../../services/telemetry/telemetry.session";
import { SidebarStore } from "../s25-sidebar/s25.sidebar.store";
import { effect } from "@angular/core";

@TypeManagerDecorator("s25-ng-ai-pricing-formula-chatbot")
@Component({
    selector: "s25-ng-ai-pricing-formula-chatbot",
    template: `
        <s25-ng-ai-chatbot
            #chatbot
            [welcomeMessage]="
                'Provide the details of the pricing for this rate schedule and I will help create the necessary formulas.'
            "
            [mode]="'fixed'"
            [title]="'AI Pricing Formula Chat'"
            [serviceConfig]="'pricingFormula'"
            [aiSession]="aiSession"
            (responseReceived)="onResponseReceived($event)"
        ></s25-ng-ai-chatbot>
    `,
    styles: ``,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    imports: [FormsModule, S25ChatbotComponent],
    standalone: true,
})
export class S25PricingChatbotComponent {
    @ViewChild("chatbot") chatbot!: S25ChatbotComponent<"pricingFormula">;

    readonly aiSession: TelemetrySession = AiTelemetry.createSession("PricingFormula");

    constructor(private sidebarStore: SidebarStore) {
        effect(() => {
            if (sidebarStore.isOpen()) {
                this.aiSession.open();
            } else {
                this.aiSession.close();
            }
        });
    }

    async onResponseReceived(response: AiPricingFormulaResponse) {
        if (!response.solution) {
            return this.chatbot.throwError();
        }

        if (!response.solution.filter((s) => s.formula !== "").length) {
            const explanations = response.solution.map((s) => {
                return s.explanation;
            });
            return this.chatbot.addResponse(explanations.join("\n"));
        }

        const docGroups = new Map<string, Set<string>>();

        const formulasText = response.solution
            .map((step, index) => {
                step.variables_used?.forEach((v) => {
                    if (!v.details || !v.variable) return;

                    if (!docGroups.has(v.details)) {
                        docGroups.set(v.details, new Set());
                    }

                    docGroups.get(v.details)!.add(v.variable);
                });

                const formula = step.formula && `Formula: ${step.formula}`;
                const duration = step.duration_range && `Duration: ${step.duration_range}`;
                const time = step.time_range && `Time Range: ${step.time_range}`;
                const explanation = step.explanation && `Explanation: ${step.explanation}`;

                const parts = [formula, duration, time, explanation].filter(Boolean);
                return `Formula ${index + 1}:\n${parts.join("\n")}`;
            })
            .join("\n\n");

        const documentationText =
            docGroups.size &&
            `\n\nVariables used:\n` +
                Array.from(docGroups.entries())
                    .map(([details, variables]) => `${Array.from(variables).join(", ")}\n${details}`)
                    .join("\n\n");

        this.chatbot.addResponse(formulasText + documentationText);
    }
}
